import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { useData } from '../../context/DataContext';
import ProjectDetails from './ProjectDetails';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUsers, FiClock, FiEye, FiEdit2, FiFolder } = FiIcons;

const ProjectList = ({ projects, onEditProject }) => {
  const { teamMembers } = useData();
  const [selectedProject, setSelectedProject] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'planning': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTeamAvatars = (teamIds) => {
    return teamIds.map(id => teamMembers.find(m => m.id === id)).filter(Boolean);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {projects.map((project, index) => {
            const team = getTeamAvatars(project.teamIds || []);
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => setSelectedProject(project)}
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <SafeIcon icon={FiEye} className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => onEditProject(project)}
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate">
                  {project.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">
                  {project.description}
                </p>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <div className="flex -space-x-2">
                      {team.slice(0, 3).map((member, i) => (
                        <img 
                          key={member.id} 
                          src={member.avatar} 
                          alt={member.name} 
                          className="w-8 h-8 rounded-full border-2 border-white"
                          title={member.name}
                        />
                      ))}
                      {team.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs text-gray-600 font-medium">
                          +{team.length - 3}
                        </div>
                      )}
                      {team.length === 0 && (
                        <span className="text-xs text-gray-400 italic">No members</span>
                      )}
                    </div>
                    <div className="flex items-center text-gray-500 text-xs">
                      <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-1" />
                      {formatDistanceToNow(new Date(project.deadline), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <SafeIcon icon={FiFolder} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
          <p className="text-gray-600">
            Create your first project to get started.
          </p>
        </div>
      )}

      <AnimatePresence>
        {selectedProject && (
          <ProjectDetails 
            key="project-details-modal"
            project={selectedProject} 
            onClose={() => setSelectedProject(null)} 
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectList;